import { useState } from "react";
import { useNavigate } from "react-router-dom";
import { User, Mail, LogOut, Edit2, Check, X } from "lucide-react";
import { useAuth } from "../contexts/AuthContext";

export default function ProfilePage() {
  const { user, logout } = useAuth();
  const navigate = useNavigate();
  const [editing, setEditing] = useState(false);
  const [name, setName] = useState(user?.name || "");

  const handleLogout = async () => {
    await logout();
    navigate("/");
  };

  if (!user) {
    navigate("/auth");
    return null;
  }

  const roleBadge = {
    admin: { label: "Administrateur", color: "bg-purple-100 text-purple-700" },
    vendor: { label: "Vendeur", color: "bg-blue-100 text-blue-700" },
    customer: { label: "Client", color: "bg-green-100 text-green-700" },
  }[user.role] || { label: user.role, color: "bg-gray-100 text-gray-600" };

  return (
    <div className="bg-[#F9FAFB] min-h-screen pb-24 md:pb-8">
      <div className="w-full max-w-lg mx-auto px-4 py-6">
        <h1 className="text-xl font-extrabold text-[#232F3E] mb-5" data-testid="profile-title">Mon profil</h1>

        <div className="bg-white border border-gray-200 rounded-md p-5">
          {/* Avatar */}
          <div className="flex flex-col items-center mb-5">
            <div className="w-20 h-20 rounded-full bg-[#FF9900] flex items-center justify-center mb-3">
              <span className="text-3xl font-black text-[#111827]">{user.name?.[0]?.toUpperCase()}</span>
            </div>
            <span className={`text-xs font-semibold px-2.5 py-1 rounded ${roleBadge.color}`}>{roleBadge.label}</span>
          </div>

          {/* Info */}
          <div className="space-y-3">
            <div className="flex items-center gap-3 p-3 bg-[#F9FAFB] rounded-md">
              <User size={16} className="text-gray-400 shrink-0" />
              <div className="flex-1">
                <p className="text-xs text-gray-500">Nom complet</p>
                <p className="font-medium text-[#232F3E] text-sm">{user.name}</p>
              </div>
            </div>
            <div className="flex items-center gap-3 p-3 bg-[#F9FAFB] rounded-md">
              <Mail size={16} className="text-gray-400 shrink-0" />
              <div className="flex-1">
                <p className="text-xs text-gray-500">{user.role === "vendor" ? "Numéro WhatsApp" : "Adresse email"}</p>
                <p className="font-medium text-[#232F3E] text-sm">{user.whatsapp || user.email}</p>
              </div>
            </div>
          </div>

          {/* Dashboard links */}
          {user.role === "vendor" && (
            <button
              onClick={() => navigate("/dashboard/vendeur")}
              className="w-full mt-4 border border-[#FF9900] text-[#FF9900] font-bold py-2.5 rounded-md hover:bg-orange-50 transition-colors text-sm"
              data-testid="go-to-vendor-dashboard"
            >
              Accéder à mon tableau de bord vendeur
            </button>
          )}
          {user.role === "admin" && (
            <button
              onClick={() => navigate("/dashboard/admin")}
              className="w-full mt-4 border border-[#232F3E] text-[#232F3E] font-bold py-2.5 rounded-md hover:bg-gray-50 transition-colors text-sm"
              data-testid="go-to-admin-dashboard"
            >
              Administration
            </button>
          )}

          <button
            onClick={() => navigate("/commandes")}
            className="w-full mt-2 border border-gray-300 text-gray-700 font-medium py-2.5 rounded-md hover:bg-gray-50 transition-colors text-sm"
            data-testid="go-to-orders"
          >
            Mes commandes
          </button>

          {/* Logout */}
          <button
            onClick={handleLogout}
            className="w-full mt-3 flex items-center justify-center gap-2 bg-red-50 text-red-600 border border-red-200 font-bold py-2.5 rounded-md hover:bg-red-100 transition-colors text-sm"
            data-testid="logout-profile-btn"
          >
            <LogOut size={15} /> Se déconnecter
          </button>
        </div>

        <p className="text-center text-xs text-gray-400 mt-5">
          Membre depuis {user.created_at ? new Date(user.created_at).toLocaleDateString("fr-FR", { month: "long", year: "numeric" }) : "récemment"}
        </p>
      </div>
    </div>
  );
}
